/**
 * Helpers for the court×time grid (ported from the main app's booking-helpers).
 * Adapted to the sidecar slot shape — a slot only needs id/startTime/status and,
 * once booked, a courtNumber. Planned slots have no court yet, so they are
 * assigned a target court for the visualisation.
 */
/** Court number from a BeWell locationId suffix, e.g. "ZJOSCSPH04" → 4. */
export function locationToCourtNumber(locationId) {
    const suffix = locationId.slice(-2);
    return parseInt(suffix, 10) || 0;
}
/** Format an ISO datetime as "HH:mm" (Europe/London, 24-hour). */
export function formatTimeShort(iso) {
    return new Date(iso).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Europe/London",
    });
}
/**
 * Assign planned slots to target courts for the grid.
 *  1. Continuity first — prefer a court a booked/assigned slot uses at an
 *     adjacent time, so the grid shows a continuous block down a column.
 *  2. Otherwise fall back to court priority order (courtOrder, else 1..4).
 */
export function assignPlannedCourts(planned, booked, order) {
    const priority = order.length > 0 ? order : [1, 2, 3, 4];
    const bookedByTime = new Map();
    for (const s of booked) {
        if (s.courtNumber == null)
            continue;
        const set = bookedByTime.get(s.startTime) ?? new Set();
        set.add(s.courtNumber);
        bookedByTime.set(s.startTime, set);
    }
    const allTimes = [
        ...new Set([...booked.map((s) => s.startTime), ...planned.map((s) => s.startTime)]),
    ].sort();
    const timeIndex = new Map(allTimes.map((t, i) => [t, i]));
    const courtsAtTime = new Map();
    for (const [t, courts] of bookedByTime)
        courtsAtTime.set(t, new Set(courts));
    const result = new Map();
    const sortedPlanned = [...planned].sort((a, b) => a.startTime.localeCompare(b.startTime));
    for (const s of sortedPlanned) {
        const taken = courtsAtTime.get(s.startTime) ?? new Set();
        const idx = timeIndex.get(s.startTime) ?? -1;
        const adjacentCourts = [];
        for (const offset of [-1, 1]) {
            const adjTime = allTimes[idx + offset];
            if (!adjTime)
                continue;
            const adjCourts = courtsAtTime.get(adjTime);
            if (!adjCourts)
                continue;
            for (const c of adjCourts)
                if (!adjacentCourts.includes(c))
                    adjacentCourts.push(c);
        }
        const target = adjacentCourts.find((c) => !taken.has(c)) ??
            priority.find((c) => !taken.has(c)) ??
            priority[0];
        result.set(s.id, target);
        const set = courtsAtTime.get(s.startTime) ?? new Set();
        set.add(target);
        courtsAtTime.set(s.startTime, set);
    }
    return result;
}
/**
 * Court numbers to render when the venue court count is unknown: the booked
 * courts, else 1..N from the busiest timeslot's slot count.
 */
export function deriveCourts(slots) {
    const courtSet = new Set();
    for (const s of slots)
        if (s.courtNumber != null)
            courtSet.add(s.courtNumber);
    if (courtSet.size === 0) {
        const perTime = new Map();
        for (const s of slots)
            perTime.set(s.startTime, (perTime.get(s.startTime) ?? 0) + 1);
        const count = Math.max(...[...perTime.values()], 1);
        return Array.from({ length: Math.min(count, 8) }, (_, i) => i + 1);
    }
    return [...courtSet].sort((a, b) => a - b);
}
